import React, { useMemo, useState } from "react";
import VStack from "../../ui_atoms/VStack";
import {
    CctDetailsFieldFullValue,
    CctDetailsFieldValueInput,
    Contract,
    Mission,
} from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import { useLazyQuery, useQuery } from "@apollo/client";
import { GET_CCT_DETAILS_FIELD_VALUES, GET_CCT_DETAILS_FIELDS } from "../../graphql/cct";
import SelectField from "../SelectField";
import { CctDetailsField, CctDetailsFieldValue } from "../../__generated__/graphql";
import { getAge, hasLength, range } from "../../utils";
import { Buildable } from "ts-essentials";
import { useContractCct } from "../../hooks/cct-utils";
import { SelectItemType } from "../select/SelectChooser";
import { BodySmallText, LinkSmallText } from "../FormattedText";
import { useIntercom } from "../../hooks/intercom";
import Loading from "../Loading";
import Gap from "../../ui_atoms/Gap";
import { useTheme } from "../../hooks/use-theme";
import ScreenCardHeadband from "../../screens/ScreenCardHeadband";

const AUTO_FILLED_FIELDS = {
    YEAR: ["Jahr", "Année"],
    AGE: ["Alter", "Âge"],
};

type MissionCctDetailsProps = {
    mission: Buildable<Mission>;
    setCctDetails: (cctDetails: CctDetailsFieldFullValue) => void;
    setScreenIsValid: (value: boolean) => void;
};

const MissionCctDetails: React.FC<MissionCctDetailsProps> = ({
    mission,
    setCctDetails,
    setScreenIsValid,
}) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const {
        data: allFields,
        error: getFieldsError,
        loading: getFieldLoading,
    } = useQuery(GET_CCT_DETAILS_FIELDS, {
        fetchPolicy: "no-cache",
        variables: {
            cctEditionId: mission.currentContract?.cctEditionId || "",
        },
    });
    const { presentArticleId } = useIntercom();
    const missionCct = useContractCct(mission.currentContract as Contract);
    const missionStateCode = useMemo(
        () => mission.currentContract?.workingAddress?.locality?.state?.code || "",
        [mission],
    );

    const [getCctDetailsFieldValues] = useLazyQuery(GET_CCT_DETAILS_FIELD_VALUES);

    const [loadingError, setLoadingError] = useState<string>();

    // this array contains all fields that are flattened (all child of field are placed just after their parent).
    const [flattenedFields, setFlattenedFields] = useState<CctDetailsField[]>([]);
    // this array has the same size of flattenedFields and contains array of possible
    // values for the field at same index. As children values depend on parent value, they will be fetched
    // when parent value is set.
    // we use a ref as we just need this array to be mutable and we do not want to trigger a rerender when it changes.
    const flattenedValuesRef = React.useRef<CctDetailsFieldValue[][]>([]);

    React.useEffect(() => {
        // This effect is used when we load the screen and already have cct details. We need to check if we are full to set the complete flag
        if (flattenedFields && flattenedFields.length > 0) {
            //when we first receive the flattened fields we check if cct details is complete or not.
            const missionCctFields = mission.currentContract
                ?.cctDetails as CctDetailsFieldFullValue;
            setScreenIsValid(missionCctFields.tempdataCallValues.length === flattenedFields.length);
            setCctDetails(missionCctFields);
        }
    }, [flattenedFields, setCctDetails]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        // This effect should run only once when the fields for the CCT are fetched.
        // this effect flatten the tree of fields.
        if (!getFieldLoading && allFields?.getCctDetailsFields) {
            const flatten = (fields: CctDetailsField[]) => {
                const result: typeof fields = [];
                for (const f of fields) {
                    result.push(f);
                    if (f.children?.length) {
                        result.push(...flatten(f.children));
                    }
                }
                return result;
            };

            const _flattenedFields = flatten(allFields.getCctDetailsFields as CctDetailsField[]);
            setFlattenedFields(_flattenedFields);
            flattenedValuesRef.current = _flattenedFields.map((f, index) => {
                // we filter KantonRegin values based on mission state code. Then if we have only 1 value left, it will
                // be autoselect by default
                if (["KantonRegion"].includes(f.uniqName)) {
                    return f.values.filter((fideldValueToFilter) => {
                        return fideldValueToFilter.value?.includes(missionStateCode);
                    });
                }

                if (flattenedValuesRef.current[index]?.[0]?.criteriaStructureId === f.id) {
                    return flattenedValuesRef.current[index];
                }
                return f.values;
            });
            setCctDetails({
                tempdataCallValues: [],
                readableValues: [],
            });
            if (_flattenedFields.length === 0) {
                setScreenIsValid(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allFields, missionStateCode, getFieldLoading]);

    const onSelectFieldValue = React.useCallback(
        async (field: CctDetailsField, value: CctDetailsFieldValue) => {
            if (flattenedFields && mission.currentContract?.cctEditionId) {
                const fieldIndex = flattenedFields.indexOf(field);
                // We use a trick of JSON.stringify and JSON.parse to remove the immutability set by recoil on the
                // mission.currentContract.cctDetails.
                // if we try to use the spread operator the new object will be unmuttable
                const cctDetailsCopy = JSON.parse(
                    JSON.stringify(mission.currentContract.cctDetails),
                ) as CctDetailsFieldFullValue;
                if (cctDetailsCopy?.tempdataCallValues) {
                    cctDetailsCopy.tempdataCallValues[fieldIndex] = value;
                    cctDetailsCopy.readableValues[fieldIndex] = {
                        question: field.name,
                        answer: value.value || "",
                    };

                    // clean all next fields
                    cctDetailsCopy.tempdataCallValues.length = fieldIndex + 1;

                    // we potentially need to fetch child field values
                    if (field.children?.length) {
                        // fetch children values
                        for (const child of field.children) {
                            const childGlobalIndex = flattenedFields.indexOf(child);
                            if (value.value === null) {
                                // if current value is an empty value we do not fetch
                                // children value as we know children will be empty value (and APi will crash anyway)
                                flattenedValuesRef.current[childGlobalIndex] = [
                                    {
                                        value: null,
                                        criteriaStructureId: child.id,
                                        criteriaListEntryId: null,
                                    },
                                ];
                                continue;
                            }
                            const childValues = await getCctDetailsFieldValues({
                                variables: {
                                    cctEditionId: mission.currentContract.cctEditionId,
                                    criteriaStructureId: child.id,
                                    parentValue: {
                                        value: value.value,
                                        criteriaListEntryId: value.criteriaListEntryId,
                                        criteriaStructureId: value.criteriaStructureId,
                                    },
                                },
                            });
                            if (childValues.error) {
                                setLoadingError(
                                    i18n.t(
                                        "screens.user.create_mission_cct_details.error_loading_fields",
                                    ),
                                );
                                return;
                            }
                            if (childValues.data) {
                                setLoadingError(undefined);
                                flattenedValuesRef.current[childGlobalIndex] =
                                    childValues.data.getCctDetailsFieldValues.map((v) => ({
                                        value: v.value,
                                        criteriaStructureId: v.criteriaStructureId,
                                        criteriaListEntryId: v.criteriaListEntryId,
                                    }));
                            }
                        }
                    }
                    setScreenIsValid(
                        cctDetailsCopy.tempdataCallValues.length === flattenedFields.length,
                    );
                    setCctDetails(cctDetailsCopy);
                }
            }
        },
        [flattenedFields, i18n, setCctDetails, setScreenIsValid, mission, getCctDetailsFieldValues],
    );

    React.useEffect(() => {
        // This effect auto select the last field if there is only one value
        const nbDetails = mission?.currentContract?.cctDetails?.tempdataCallValues?.length || 0;
        if (!getFieldLoading && nbDetails < flattenedFields.length && flattenedFields.length > 0) {
            const nextField = flattenedFields[nbDetails];
            const nextFieldValues = flattenedValuesRef.current[nbDetails];
            if (nextFieldValues?.length === 1) {
                void onSelectFieldValue(nextField, {
                    value: nextFieldValues[0].value,
                    criteriaStructureId: nextFieldValues[0].criteriaStructureId,
                    criteriaListEntryId: nextFieldValues[0].criteriaListEntryId,
                    autoSelected: true,
                });
            } else if (nextFieldValues && AUTO_FILLED_FIELDS["YEAR"].includes(nextField.uniqName)) {
                // we auto select current year
                const currentYear = new Date().getFullYear();
                const valueIndex = nextFieldValues.findIndex(
                    (searchElement) => searchElement.value === currentYear.toString(),
                );
                if (valueIndex >= 0) {
                    void onSelectFieldValue(nextField, {
                        value: nextFieldValues[valueIndex].value,
                        criteriaStructureId: nextFieldValues[valueIndex].criteriaStructureId,
                        criteriaListEntryId: nextFieldValues[valueIndex].criteriaListEntryId,
                        autoSelected: true,
                    });
                }
            } else if (nextFieldValues && AUTO_FILLED_FIELDS["AGE"].includes(nextField.uniqName)) {
                const age = mission.workerInfo?.birthdate
                    ? getAge(new Date(mission.workerInfo?.birthdate || ""))
                    : null;
                if (age) {
                    let valueIndex = -1;
                    // if age is bigger than the last entry of ages, we select the last entry.
                    // if last entry is empty, we use 200 to be sure age of worker will be smaller. So in this case
                    // we won't auto select.
                    if (
                        age > parseInt(nextFieldValues[nextFieldValues.length - 1].value || "200")
                    ) {
                        valueIndex = nextFieldValues.length - 1;
                    } else {
                        // we auto select the age if the field is the Age field
                        valueIndex = nextFieldValues.findIndex(
                            (searchElement) => searchElement.value === age.toString(),
                        );
                    }
                    if (valueIndex >= 0) {
                        void onSelectFieldValue(nextField, {
                            value: nextFieldValues[valueIndex].value,
                            criteriaStructureId: nextFieldValues[valueIndex].criteriaStructureId,
                            criteriaListEntryId: nextFieldValues[valueIndex].criteriaListEntryId,
                            autoSelected: true,
                        });
                    }
                }
            }
        }
    }, [
        mission.currentContract?.cctDetails?.tempdataCallValues,
        flattenedFields,
        mission.workerInfo?.birthdate,
        onSelectFieldValue,
        getFieldLoading,
    ]);

    // If readableValues is defined, all useEffect has finished processing and eventual autofields's values has been pre-filled
    const isReadableValuesReady = !!mission.currentContract?.cctDetails?.readableValues;

    return (
        <VStack gap={0.75} alignment="stretch">
            {getFieldsError && (
                <ScreenCardHeadband
                    text={i18n.t("screens.user.create_mission_cct_details.error_loading_fields")}
                    mode="error"
                />
            )}
            {loadingError && <ScreenCardHeadband text={loadingError} mode="error" />}
            {getFieldLoading && <Loading />}
            {!getFieldLoading &&
                isReadableValuesReady &&
                hasLength(flattenedFields) &&
                hasLength(flattenedValuesRef.current) &&
                range(
                    Math.min(
                        (mission.currentContract?.cctDetails?.tempdataCallValues?.length || 0) + 1,
                        flattenedFields.length,
                    ),
                ).map((idx) => {
                    const currentField = flattenedFields[idx];
                    if (
                        flattenedValuesRef.current[idx]?.length === 1 &&
                        flattenedValuesRef.current[idx][0].value === null
                    ) {
                        return null;
                    } else {
                        let currentCctDetailsFieldValue:
                            | SelectItemType<CctDetailsFieldValueInput>
                            | undefined = undefined;
                        if (mission.currentContract?.cctDetails?.tempdataCallValues?.[idx]) {
                            currentCctDetailsFieldValue = {
                                value: mission.currentContract?.cctDetails?.tempdataCallValues[
                                    idx
                                ] as CctDetailsFieldValueInput,
                                label:
                                    mission.currentContract?.cctDetails.tempdataCallValues[idx]
                                        ?.value || "",
                            };
                        }
                        if (currentCctDetailsFieldValue?.value?.autoSelected) {
                            return null;
                        }

                        return (
                            <React.Fragment key={currentField.id}>
                                <SelectField
                                    label={currentField.name}
                                    value={currentCctDetailsFieldValue?.value}
                                    data={
                                        (flattenedValuesRef.current[idx] &&
                                            flattenedValuesRef.current[idx].map((v) => ({
                                                value: v,
                                                label: v.value || "",
                                            }))) ||
                                        []
                                    }
                                    halfScreen
                                    isDynamicHeight
                                    onSelect={(selection) => {
                                        void onSelectFieldValue(currentField, selection.value);
                                    }}
                                />
                                {currentField.uniqName === "KantonRegion" && (
                                    <>
                                        <Gap gap="1/3" />
                                        <BodySmallText color={colors.greyAccessible}>
                                            {i18n.t(
                                                "screens.user.create_mission_cct_details.region_description",
                                            )}
                                        </BodySmallText>
                                    </>
                                )}
                            </React.Fragment>
                        );
                    }
                })}
            {!getFieldLoading && missionCct && missionCct.intercomHelpArticleId && (
                <LinkSmallText
                    onPress={() => {
                        void presentArticleId(missionCct.intercomHelpArticleId as string);
                    }}
                >
                    {i18n.t("screens.user.create_mission_cct_details.show_help")}
                </LinkSmallText>
            )}
        </VStack>
    );
};

export default MissionCctDetails;
